// =============================================================================
// ZONE EDITOR 3D - LIDAR POINTS REAL-TIME
// Renders LIDAR scan points as 3D dots on the table surface
// =============================================================================

import { useRef, useEffect, useCallback } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { wsService } from '../../services/websocket'
import { mmToSceneScale } from './utils'

interface LidarPoint {
  a: number  // angle in degrees
  d: number  // distance in mm
  q: number  // quality (0-60)
}

interface LidarScanData {
  points: LidarPoint[]
  count: number
  timestamp: number
}

interface LidarPoints3DProps {
  deviceId: string
  lidarX: number       // mm
  lidarY: number       // mm
  lidarRotation: number // degrees
  workspaceWidth: number  // mm
  workspaceHeight: number // mm
}

const MIN_VALID_DISTANCE = 100 // mm
const MAX_POINTS = 2000

export function LidarPoints3D({
  deviceId,
  lidarX,
  lidarY,
  lidarRotation,
  workspaceWidth,
  workspaceHeight,
}: LidarPoints3DProps) {
  const pointsRef = useRef<THREE.Points>(null)
  const positionsRef = useRef(new Float32Array(MAX_POINTS * 3))
  const colorsRef = useRef(new Float32Array(MAX_POINTS * 3))
  const countRef = useRef(0)
  const needsUpdateRef = useRef(false)

  // Max display distance: workspace diagonal * 1.5 to show points slightly beyond the table
  const maxDisplayDistance = Math.sqrt(workspaceWidth ** 2 + workspaceHeight ** 2) * 1.5

  // Convert LIDAR polar point to workspace XY (mm)
  const pointToWorkspace = useCallback((point: LidarPoint): [number, number] => {
    const totalAngleDeg = point.a + lidarRotation
    const rad = (totalAngleDeg * Math.PI) / 180
    const wx = lidarX + point.d * Math.sin(rad)
    const wy = lidarY + point.d * Math.cos(rad)
    return [wx, wy]
  }, [lidarX, lidarY, lidarRotation])

  // Subscribe to WebSocket LIDAR data
  useEffect(() => {
    const unsubscribe = wsService.subscribe((message) => {
      if (message.type === 'lidar_scan' && message.device_id === deviceId) {
        const data = message.data as LidarScanData
        if (!data.points) return

        const positions = positionsRef.current
        const colors = colorsRef.current
        let idx = 0

        // Workspace bounds with margin for points slightly outside
        const margin = Math.max(workspaceWidth, workspaceHeight) * 0.3
        const minX = -margin
        const maxX = workspaceWidth + margin
        const minY = -margin
        const maxY = workspaceHeight + margin

        for (const point of data.points) {
          if (point.d < MIN_VALID_DISTANCE) continue
          if (point.d > maxDisplayDistance) continue
          if (idx >= MAX_POINTS) break

          const [wx, wy] = pointToWorkspace(point)

          // Filter: only show points near the workspace area
          if (wx < minX || wx > maxX || wy < minY || wy > maxY) continue

          const i3 = idx * 3
          positions[i3] = mmToSceneScale(wx)
          positions[i3 + 1] = 0.003  // just above surface
          positions[i3 + 2] = -mmToSceneScale(wy)

          // Color: points inside workspace = bright green, outside = dim orange
          const insideWorkspace = wx >= 0 && wx <= workspaceWidth && wy >= 0 && wy <= workspaceHeight
          const q = Math.min(point.q, 60) / 60

          if (insideWorkspace) {
            colors[i3] = 0.1 + q * 0.2       // R
            colors[i3 + 1] = 0.5 + q * 0.5   // G - brighter
            colors[i3 + 2] = 0.2 + q * 0.2   // B
          } else {
            colors[i3] = 0.4 + q * 0.3       // R - orange tint
            colors[i3 + 1] = 0.3 + q * 0.2   // G
            colors[i3 + 2] = 0.1              // B
          }

          idx++
        }

        countRef.current = idx
        needsUpdateRef.current = true
      }
    })

    return () => unsubscribe()
  }, [deviceId, pointToWorkspace, maxDisplayDistance, workspaceWidth, workspaceHeight])

  // Update geometry each frame if new data arrived
  useFrame(() => {
    if (!needsUpdateRef.current || !pointsRef.current) return
    needsUpdateRef.current = false

    const geo = pointsRef.current.geometry
    const posAttr = geo.getAttribute('position') as THREE.BufferAttribute
    const colAttr = geo.getAttribute('color') as THREE.BufferAttribute

    posAttr.array.set(positionsRef.current)
    colAttr.array.set(colorsRef.current)

    // Only render the actual number of points
    geo.setDrawRange(0, countRef.current)

    posAttr.needsUpdate = true
    colAttr.needsUpdate = true
  })

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          array={new Float32Array(MAX_POINTS * 3)}
          count={MAX_POINTS}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          array={new Float32Array(MAX_POINTS * 3)}
          count={MAX_POINTS}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.012}
        vertexColors
        sizeAttenuation
        transparent
        opacity={0.9}
        depthWrite={false}
      />
    </points>
  )
}
